import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { unlockControls, approveAssessment, getEvidenceDownloadUrl } from 'services/vendor-assessment-service'
import i18n from 'i18next'
import { translateDomain } from '../../utils/labels_utils'

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') {
    const lang = (i18n.language || 'en').split('-')[0]
    const raw = val[lang] || val.en || val.pt || val.es || Object.values(val)[0] || ''
    return typeof raw === 'string' ? raw : String(raw)
  }
  return String(val)
}
import { Dialog, Checkbox, TextField } from 'Components/ui'
import {
  LockOpenIcon,
  LockIcon,
  DownloadIcon,
  GppBadIcon,
  GppMaybeIcon,
} from './AssessmentIcons'
import {
  Container,
  TablePaper,
  StyledTable,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  ActionBar,
  ActionBarLeft,
  ActionBarRight,
  SectionTitle,
  ControlsSelected,
  ControlDescription,
  ControlDomain,
  NoEvidenceText,
  EmptyText,
  StyledButton,
  LockChip,
  TableHeaderCellCenter,
  TableCellCenter,
  NotesSection,
  IconWrapper,
  UnlockBtn,
  ApproveBtn,
  LevelChip,
  GapChip,
} from './GapAnalysisView.styles'

export const GapAnalysisView = ({ assessment, controls, trustScore, onRefresh }) => {
  const { t } = useTranslation(['gap-analysis', 'common'])
  const queryClient = useQueryClient()

  const [selectedControls, setSelectedControls] = useState([])
  const [notes, setNotes] = useState('')

  const unlockMutation = useMutation(
    ({ controlIds, unlockNotes }) => unlockControls(assessment._id, controlIds, unlockNotes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', assessment._id])
        Dialog.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.gapView.swal.unlockSuccessTitle'),
          text: t('gap-analysis:assessment.gapView.swal.unlockSuccessText', { count: selectedControls.length }),
          timer: 2000,
        })
        setSelectedControls([])
        setNotes('')
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        Dialog.alert({
          icon: 'error',
          title: t('gap-analysis:assessment.gapView.swal.errorTitle'),
          message: error.response?.data?.message || t('gap-analysis:assessment.gapView.swal.errorUnlock'),
          elevated: true,
        })
      }
    }
  )

  const approveMutation = useMutation(
    ({ approvalNotes }) => approveAssessment(assessment._id, approvalNotes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', assessment._id])
        Dialog.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.gapView.swal.approveSuccessTitle'),
          text: t('gap-analysis:assessment.gapView.swal.approveSuccessText'),
          timer: 2000,
        })
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        Dialog.alert({
          icon: 'error',
          title: t('gap-analysis:assessment.gapView.swal.errorTitle'),
          message: error.response?.data?.message || t('gap-analysis:assessment.gapView.swal.errorApprove'),
          elevated: true,
        })
      }
    }
  )

  const handleSelectControl = (controlId) => {
    if (selectedControls.includes(controlId)) {
      setSelectedControls(selectedControls.filter(id => id !== controlId))
    } else {
      setSelectedControls([...selectedControls, controlId])
    }
  }

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const unlockableControls = controls
        .filter(c => c.response?.locked === true)
        .map(c => c._id)
      setSelectedControls(unlockableControls)
    } else {
      setSelectedControls([])
    }
  }

  const isAllUnlockableSelected = selectedControls.length > 0 &&
    selectedControls.length === controls.filter(c => c.response?.locked === true).length
  const isIndeterminate = selectedControls.length > 0 &&
    selectedControls.length < controls.filter(c => c.response?.locked === true).length

  const handleUnlock = async () => {
    if (selectedControls.length === 0) {
      Dialog.alert({
        icon: 'warning',
        title: t('gap-analysis:assessment.gapView.swal.noSelectionTitle'),
        message: t('gap-analysis:assessment.gapView.swal.noSelectionText'),
        elevated: true,
      })
      return
    }

    const notes = await Dialog.prompt({
      title: t('gap-analysis:assessment.gapView.swal.unlockTitle'),
      message: t('gap-analysis:assessment.gapView.swal.unlockHtml', { count: selectedControls.length }),
      inputLabel: t('gap-analysis:assessment.gapView.swal.notesLabel'),
      inputPlaceholder: t('gap-analysis:assessment.gapView.swal.notesPlaceholder'),
      confirmLabel: t('gap-analysis:assessment.gapView.swal.unlockButton'),
      elevated: true,
    })

    if (notes !== null) {
      unlockMutation.mutate({ controlIds: selectedControls, unlockNotes: notes || '' })
    }
  }

  const handleApprove = async () => {
    const notes = await Dialog.prompt({
      title: t('gap-analysis:assessment.gapView.swal.approveTitle'),
      message: t('gap-analysis:assessment.gapView.swal.approveHtml', { score: trustScore?.percentage ?? 'N/A' }),
      inputLabel: t('gap-analysis:assessment.gapView.swal.approvalNotesLabel'),
      inputPlaceholder: t('gap-analysis:assessment.gapView.swal.approvalNotesPlaceholder'),
      confirmLabel: t('gap-analysis:assessment.gapView.swal.approveButton'),
      elevated: true,
    })

    if (notes !== null) {
      setNotes(notes)
      approveMutation.mutate({ approvalNotes: notes || '' })
    }
  }

  const handleDownloadEvidence = async (fileId) => {
    try {
      const response = await getEvidenceDownloadUrl(fileId)
      const url = response.data.data?.url || response.data.url
      window.open(url, '_blank')
    } catch (error) {
      Dialog.alert({
        icon: 'error',
        title: t('gap-analysis:assessment.gapView.swal.downloadFailedTitle'),
        message: error.response?.data?.message || t('gap-analysis:assessment.gapView.swal.errorDownload'),
        elevated: true,
      })
    }
  }

  const getGapColor = (gap) => {
    if (gap === 0) return 'success'
    if (gap < 0) return 'success'
    return 'error'
  }

  const getGapLabel = (gap) => {
    if (gap === 0) return t('gap-analysis:assessment.gapView.gapLabels.meetsTarget')
    if (gap < 0) return t('gap-analysis:assessment.gapView.gapLabels.aboveTarget')
    return t('gap-analysis:assessment.gapView.gapLabels.belowTarget')
  }

  return (
    <Container>
      <ActionBar>
        <ActionBarLeft>
          <SectionTitle>{t('gap-analysis:assessment.gapView.title')}</SectionTitle>
          <ControlsSelected>
            {t('gap-analysis:assessment.gapView.controlsSelected', { count: selectedControls.length })}
          </ControlsSelected>
        </ActionBarLeft>
        <ActionBarRight>
          <UnlockBtn
            onClick={handleUnlock}
            disabled={selectedControls.length === 0 || unlockMutation.isLoading}
          >
            <IconWrapper><LockOpenIcon width={18} height={18} /></IconWrapper>
            {t('gap-analysis:assessment.gapView.unlockButton', { count: selectedControls.length })}
          </UnlockBtn>
          <ApproveBtn
            onClick={handleApprove}
            disabled={approveMutation.isLoading || (trustScore && trustScore.percentage < 70)}
            title={trustScore && trustScore.percentage < 70 ? t('gap-analysis:assessment.gapView.execRequired') : undefined}
          >
            {t('gap-analysis:assessment.gapView.approveButton')}
          </ApproveBtn>
        </ActionBarRight>
      </ActionBar>

      <TablePaper>
        <StyledTable>
          <TableHeader>
            <TableRow>
              <TableHeaderCellCenter>
                <Checkbox
                  onChange={handleSelectAll}
                  checked={isAllUnlockableSelected}
                  indeterminate={isIndeterminate}
                />
              </TableHeaderCellCenter>
              <TableHeaderCell>{t('gap-analysis:assessment.gapView.table.control')}</TableHeaderCell>
              <TableHeaderCell>{t('gap-analysis:assessment.gapView.table.target')}</TableHeaderCell>
              <TableHeaderCell>{t('gap-analysis:assessment.gapView.table.vendor')}</TableHeaderCell>
              <TableHeaderCell>{t('common:labels.status')}</TableHeaderCell>
              <TableHeaderCell>{t('gap-analysis:assessment.gapView.table.evidence')}</TableHeaderCell>
              <TableHeaderCell>{t('gap-analysis:assessment.gapView.table.lockStatus')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {controls.length === 0 ? (
              <TableRow>
                <TableCellCenter colSpan={7}>
                  <EmptyText>{t('gap-analysis:assessment.gapView.table.noControls')}</EmptyText>
                </TableCellCenter>
              </TableRow>
            ) : (
              controls.map((control) => {
                const gap = control.targetMaturity - (control.response?.maturityLevel ?? 0)
                const isLocked = control.response?.locked === true

                return (
                  <TableRow key={control._id}>
                    <TableCellCenter>
                      <Checkbox
                        checked={selectedControls.includes(control._id)}
                        onChange={() => handleSelectControl(control._id)}
                        disabled={!isLocked}
                      />
                    </TableCellCenter>
                    <TableCell>
                      <ControlDescription>{str(control.description)}</ControlDescription>
                      <ControlDomain>{translateDomain(t, control.domain)}</ControlDomain>
                    </TableCell>
                    <TableCell>
                      <LevelChip>{'L'}{control.targetMaturity}</LevelChip>
                    </TableCell>
                    <TableCell>
                      <LevelChip>{'L'}{control.response?.maturityLevel ?? 0}</LevelChip>
                    </TableCell>
                    <TableCell>
                      <GapChip $variant={getGapColor(gap)}>{getGapLabel(gap)}</GapChip>
                    </TableCell>
                    <TableCell>
                      {control.response?.evidenceFiles && control.response.evidenceFiles.length > 0 ? (
                        control.response.evidenceFiles.map((file) => {
                          const isDownloadable = !file.scanStatus || file.scanStatus === 'clean'
                          const isMalicious = file.scanStatus === 'malicious'
                          const isPending = file.scanStatus === 'pending'

                          const tooltipText = isMalicious
                            ? `${file.fileName} — Threat detected${file.scanResultDetail ? ': ' + file.scanResultDetail : ''}`
                            : isPending
                              ? `${file.fileName} — Scanning...`
                              : file.fileName

                          return (
                            <span key={file._id} title={tooltipText}>
                              <StyledButton
                                type="button"
                                $malicious={isMalicious}
                                onClick={() => handleDownloadEvidence(file._id)}
                                disabled={!isDownloadable}
                              >
                                {isMalicious ? (
                                  <GppBadIcon width={18} height={18} />
                                ) : isPending ? (
                                  <GppMaybeIcon width={18} height={18} />
                                ) : (
                                  <DownloadIcon width={18} height={18} />
                                )}
                              </StyledButton>
                            </span>
                          )
                        })
                      ) : (
                        <NoEvidenceText>{t('gap-analysis:assessment.gapView.table.noEvidence')}</NoEvidenceText>
                      )}
                    </TableCell>
                    <TableCell>
                      {isLocked ? (
                        <LockChip $locked>
                          <LockIcon width={14} height={14} />
                          {t('gap-analysis:assessment.gapView.table.locked')}
                        </LockChip>
                      ) : (
                        <LockChip $locked={false}>
                          <LockOpenIcon width={14} height={14} />
                          {t('gap-analysis:assessment.gapView.table.unlocked')}
                        </LockChip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </StyledTable>
      </TablePaper>

      <NotesSection>
        <TextField
          multiline
          fullWidth
          rows={3}
          placeholder={t('gap-analysis:assessment.gapView.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </NotesSection>
    </Container>
  )
}
